import { Box, Typography, useTheme } from "@mui/material";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency, formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import { Chart as ChartJS } from "chart.js/auto";
import AnnotationPlugin from "chartjs-plugin-annotation";
import type React from "react";
import { Bar } from "react-chartjs-2";

ChartJS.register(AnnotationPlugin);

type Props = {
  readonly immovableAssetsAmount: number;
  readonly movableAssetsAmount: number;
  readonly financialAssetsAmount: number;
  readonly liabilitiesAmount: number;
};

export const AssetSummaryChart: React.FC<Props> = ({
  immovableAssetsAmount,
  movableAssetsAmount,
  financialAssetsAmount,
  liabilitiesAmount,
}) => {
  const theme = useTheme();
  const { t } = useResource();

  const isLabelVisibleByValues = (value: number): boolean => {
    const maxValue = Math.max(immovableAssetsAmount, movableAssetsAmount, financialAssetsAmount);
    return value >= maxValue * 0.12;
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        mode: "nearest" as const,
        intersect: true,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || "";

            if (label) {
              const datasetName = context.dataset.label;

              switch (datasetName) {
                case t(Resources.AnalysisDetail.Assets.Financial.Title): {
                  return `${t(Resources.AnalysisDetail.Assets.Financial.Title)}: ${formatCurrency(financialAssetsAmount, CurrencyCode.CZK, 0)}`;
                }
                case t(Resources.AnalysisDetail.Assets.Movable.Title): {
                  return `${t(Resources.AnalysisDetail.Assets.Movable.Title)}: ${formatCurrency(movableAssetsAmount, CurrencyCode.CZK, 0)}`;
                }
                case t(Resources.AnalysisDetail.Assets.Immovable.Title): {
                  return `${t(Resources.AnalysisDetail.Assets.Immovable.Title)}: ${formatCurrency(immovableAssetsAmount, CurrencyCode.CZK, 0)}`;
                }
                case t(Resources.AnalysisDetail.Assets.Liabilities.Title): {
                  return `${t(Resources.AnalysisDetail.Assets.Liabilities.Title)}: ${formatCurrency(liabilitiesAmount, CurrencyCode.CZK, 0)}`;
                }

                default:
                  throw new Error();
              }
            }
          },
        },
      },
      annotation: {
        annotations: {
          immovableAssetsLabel: {
            display: isLabelVisibleByValues(immovableAssetsAmount),
            type: "label" as const,
            xValue: 0,
            yValue: immovableAssetsAmount / 2 + movableAssetsAmount + financialAssetsAmount,
            backgroundColor: "transparent",
            content: formatCurrencyShortText(immovableAssetsAmount),
            color: colors.chartInfoDark,
            font: {
              weight: "bold" as const,
              size: 12,
            },
          },
          movableAssetsLabel: {
            display: isLabelVisibleByValues(movableAssetsAmount),
            type: "label" as const,
            xValue: 0,
            yValue: movableAssetsAmount / 2 + financialAssetsAmount,
            backgroundColor: "transparent",
            content: formatCurrencyShortText(movableAssetsAmount),
            color: colors.white,
            font: {
              weight: "bold" as const,
              size: 12,
            },
          },
          financialAssetsLabel: {
            display: isLabelVisibleByValues(financialAssetsAmount),
            type: "label" as const,
            xValue: 0,
            yValue: financialAssetsAmount / 2,
            backgroundColor: "transparent",
            content: formatCurrencyShortText(financialAssetsAmount),
            color: colors.white,
            font: {
              weight: "bold" as const,
              size: 12,
            },
          },
          liabilitiesAmountLabel: {
            display: isLabelVisibleByValues(liabilitiesAmount),
            type: "label" as const,
            xValue: 1,
            yValue: liabilitiesAmount / 2,
            backgroundColor: "transparent",
            content: formatCurrencyShortText(liabilitiesAmount),
            color: colors.white,
            font: {
              weight: "bold" as const,
              size: 12,
            },
          },
        },
      },
    },
    interaction: {
      mode: "nearest" as const,
      intersect: true,
      axis: "x" as const,
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: colors.textSecondary,
        },
      },
      y: {
        beginAtZero: true,
        stacked: true,
        ticks: {
          display: false,
        },
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        grace: "1",
      },
    },
  };

  return (
    <Box>
      <Box display="flex" alignItems="end">
        <Box position="relative" width={"100%"} minHeight={210}>
          <Bar
            options={options}
            data={{
              labels: [t(Resources.AnalysisDetail.Assets.AssetSummary.Assets), t(Resources.AnalysisDetail.Assets.AssetSummary.Liabilities)],
              datasets: [
                {
                  label: t(Resources.AnalysisDetail.Assets.Financial.Title),
                  data: [financialAssetsAmount, 0],
                  backgroundColor: colors.chartInfoDark,
                  borderRadius: 0,
                  barPercentage: 1.15,
                },
                {
                  label: t(Resources.AnalysisDetail.Assets.Movable.Title),
                  data: [movableAssetsAmount, 0],
                  backgroundColor: theme.palette.info.main,
                  borderRadius: 0,
                  barPercentage: 1.15,
                },
                {
                  label: t(Resources.AnalysisDetail.Assets.Immovable.Title),
                  data: [immovableAssetsAmount, 0],
                  backgroundColor: theme.palette.info.light,
                  borderRadius: {
                    topLeft: 6,
                    topRight: 6,
                    bottomLeft: 0,
                    bottomRight: 0,
                  },
                  barPercentage: 1.15,
                },
                {
                  label: t(Resources.AnalysisDetail.Assets.Liabilities.Title),
                  data: [0, liabilitiesAmount],
                  backgroundColor: theme.palette.error.main,
                  borderRadius: {
                    topLeft: 6,
                    topRight: 6,
                    bottomLeft: 0,
                    bottomRight: 0,
                  },
                  barPercentage: 1.15,
                },
              ],
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
